package org.gymly.ui.admin

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Check
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminProductLicenseStatus
import org.gymly.theme.AdminColors
import org.gymly.util.userMessage

/** El botón de validar solo existe si la licencia incluye portal web o app móvil, y está activa. */
fun AdminProductLicenseStatus.allowsProofValidation(): Boolean {
    val active = status.equals("ACTIVE", true) || status.equals("GRACE", true)
    return active && features.any {
        it.equals("portal", true) || it.equals("mobile_android", true) || it.equals("mobile_ios", true)
    }
}

/** Lee la licencia una vez por pantalla. Si no responde, no se muestra el botón. */
@Composable
fun rememberProofValidationAllowed(): Boolean {
    var allowed by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        allowed = runCatching { AdminRepo.api.getProductLicenseStatus().allowsProofValidation() }
            .getOrDefault(false)
    }
    return allowed
}

/**
 * Clave de referencia, igual que el backend: últimos 5 dígitos si hay al menos 5; si no, el texto en minúsculas.
 * Vacía si tiene menos de 5 caracteres.
 */
fun referenceLookupKey(raw: String?): String {
    val normalized = raw?.trim()?.replaceFirst(Regex("(?i)^ref:?\\s*"), "")?.trim().orEmpty()
    val digits = normalized.filter { it.isDigit() }
    val key = if (digits.length >= 5) digits.takeLast(5) else normalized.lowercase()
    return if (key.length >= 5) key else ""
}

/** Icono de validar. Deshabilitado si el cobro o la venta no está "Por validar". */
@Composable
fun ProofValidateButton(enabled: Boolean, onClick: () -> Unit) {
    IconButton(onClick = onClick, enabled = enabled) {
        Icon(
            Icons.Outlined.Check,
            "Validar comprobante",
            tint = if (enabled) Color(0xFF1565C0) else AdminColors.TextMuted,
        )
    }
}

/**
 * Modal con dos opciones: Pagado, o Rechazado (pide el motivo).
 * [onDecide] hace la validación; si falla, el error queda en el modal. Quien llama debe capturar
 * AdminPendingApprovalException (solicitud enviada a Aprobaciones) y avisar, sin relanzarla.
 */
@Composable
fun ProofValidateDialog(
    amountLabel: String,
    onDismiss: () -> Unit,
    onDecide: suspend (paid: Boolean, reason: String?) -> Unit,
    /** Referencia del pago (nota) y su fecha de pago: si ya existe ese día en otro pago o venta, se avisa. */
    reference: String? = null,
    paymentDateIso: String? = null,
    excludePaymentId: String? = null,
    excludeStoreSaleId: String? = null,
) {
    val scope = rememberCoroutineScope()
    var rejecting by remember { mutableStateOf(false) }
    var reason by remember { mutableStateOf("") }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var duplicateWarning by remember { mutableStateOf<String?>(null) }

    // Solo informa: el comprobante se puede validar igual.
    LaunchedEffect(reference, paymentDateIso) {
        val key = referenceLookupKey(reference)
        val day = paymentDateIso?.take(10)
        if (key.isEmpty() || day.isNullOrBlank()) return@LaunchedEffect
        val where = loadRegisteredPaymentRefs(day, excludePaymentId, excludeStoreSaleId)[key]
        duplicateWarning = where?.let {
            "Alerta: la referencia $key ya fue registrada en $it con la misma fecha de pago. Puedes validarla igual."
        }
    }

    fun decide(paid: Boolean) {
        scope.launch {
            busy = true
            error = null
            try {
                onDecide(paid, if (paid) null else reason.trim())
                onDismiss()
            } catch (e: Exception) {
                error = e.userMessage("No se pudo validar el comprobante.")
            } finally {
                busy = false
            }
        }
    }

    AlertDialog(
        onDismissRequest = { if (!busy) onDismiss() },
        title = { Text("Validar comprobante", fontWeight = FontWeight.Bold) },
        text = {
            Column {
                Text(amountLabel, fontSize = 14.sp)
                duplicateWarning?.let {
                    Spacer(Modifier.height(8.dp))
                    Text(it, color = Color(0xFFEF6C00), fontSize = 13.sp)
                }
                if (rejecting) {
                    Spacer(Modifier.height(12.dp))
                    OutlinedTextField(
                        value = reason,
                        onValueChange = { reason = it },
                        label = { Text("Motivo del rechazo") },
                        minLines = 2,
                        enabled = !busy,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
                error?.let {
                    Spacer(Modifier.height(10.dp))
                    Text(it, color = AdminColors.Error, fontSize = 13.sp)
                }
            }
        },
        confirmButton = {
            if (rejecting) {
                Button(
                    onClick = { decide(false) },
                    enabled = !busy && reason.isNotBlank(),
                    colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Error),
                ) { Text("Confirmar rechazo") }
            } else {
                Button(
                    onClick = { decide(true) },
                    enabled = !busy,
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2E7D32)),
                ) { Text("Pagado") }
            }
        },
        dismissButton = {
            if (rejecting) {
                TextButton(onClick = { rejecting = false }, enabled = !busy) { Text("Volver") }
            } else {
                TextButton(onClick = { rejecting = true }, enabled = !busy) {
                    Text("Rechazado", color = AdminColors.Error)
                }
            }
        },
    )
}
